import { PLANNED_MAX, PLANNED_MIN } from './validate-log'

export type TimerPreset = { focus: number; rest: number }

export const PRESETS: readonly TimerPreset[] = [
  { focus: 25, rest: 5 },
  { focus: 50, rest: 10 },
  { focus: 90, rest: 15 },
]

/** プリセットの後ろに置く「自分で決める」の番号 */
export const CUSTOM = PRESETS.length

export const FOCUS_MIN = PLANNED_MIN
export const FOCUS_MAX = PLANNED_MAX
export const REST_MIN = 1
export const REST_MAX = 60

export const DEFAULT_CUSTOM: TimerPreset = { focus: 10, rest: 2 }

type Common = { preset: number; custom: TimerPreset }

/** 記録済みの回。店に入り直したとき、受け取り直させずに同じレシートを出すために覚えておく */
export type SavedLog = {
  id: string
  publicUrl: string | null
  focusSeconds: number
  note: string
  seat: number | null
}

export type TimerState = Common &
  (
    | { phase: 'idle' }
    | { phase: 'focus'; startedAt: number; plannedMinutes: number }
    | { phase: 'done'; startedAt: number; endedAt: number; plannedMinutes: number; saved?: SavedLog }
    | { phase: 'rest'; startedAt: number; plannedMinutes: number }
  )

export type TimerAction =
  | { type: 'select'; preset: number }
  | { type: 'setCustom'; custom: TimerPreset }
  | { type: 'start'; now: number }
  | { type: 'cancel' }
  | { type: 'tick'; now: number }
  | { type: 'saved'; saved: SavedLog }
  | { type: 'rest'; now: number }
  | { type: 'finishRest' }
  | { type: 'restore'; state: unknown }

/** 選んでいる時間の組。プリセットか、自分で決めた時間 */
export function durationsOf(s: Common): TimerPreset {
  return s.preset === CUSTOM ? s.custom : PRESETS[s.preset]
}

const inRange = (v: unknown, min: number, max: number): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max

export function isValidCustom(v: unknown): v is TimerPreset {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return inRange(o.focus, FOCUS_MIN, FOCUS_MAX) && inRange(o.rest, REST_MIN, REST_MAX)
}

const idleOf = (s: Common): TimerState => ({ phase: 'idle', preset: s.preset, custom: s.custom })

export function timerReducer(s: TimerState, a: TimerAction): TimerState {
  switch (a.type) {
    case 'select':
      if (s.phase !== 'idle' || !inRange(a.preset, 0, CUSTOM)) return s
      return { ...idleOf(s), preset: a.preset }
    case 'setCustom':
      if (s.phase !== 'idle' || !isValidCustom(a.custom)) return s
      return { ...idleOf(s), custom: { focus: a.custom.focus, rest: a.custom.rest } }
    case 'start':
      // 記録を受け取ったあとなら、休憩を挟まずに次の集中へ進める
      if (s.phase !== 'idle' && !(s.phase === 'done' && s.saved)) return s
      return { ...idleOf(s), phase: 'focus', startedAt: a.now, plannedMinutes: durationsOf(s).focus }
    case 'cancel':
      return s.phase === 'focus' ? idleOf(s) : s
    case 'tick': {
      if (s.phase !== 'focus' && s.phase !== 'rest') return s
      const end = s.startedAt + s.plannedMinutes * 60_000
      if (a.now < end) return s
      if (s.phase === 'rest') return idleOf(s)
      return {
        ...idleOf(s),
        phase: 'done',
        startedAt: s.startedAt,
        endedAt: end,
        plannedMinutes: s.plannedMinutes,
      }
    }
    case 'saved':
      if (s.phase !== 'done' || !isSavedLog(a.saved)) return s
      return { ...s, saved: a.saved }
    case 'rest':
      if (s.phase !== 'done') return s
      return { ...idleOf(s), phase: 'rest', startedAt: a.now, plannedMinutes: durationsOf(s).rest }
    case 'finishRest':
      return s.phase === 'rest' ? idleOf(s) : s
    case 'restore':
      return parseTimerState(a.state) ?? s
  }
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

function isSavedLog(v: unknown): v is SavedLog {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.id === 'string' &&
    (o.publicUrl === null || typeof o.publicUrl === 'string') &&
    isNum(o.focusSeconds) &&
    typeof o.note === 'string' &&
    (o.seat === null || isNum(o.seat))
  )
}

/** 保存されていた状態を検証して戻す。壊れていれば null */
export function parseTimerState(v: unknown): TimerState | null {
  if (typeof v !== 'object' || v === null) return null
  const o = v as Record<string, unknown>
  if (!inRange(o.preset, 0, CUSTOM)) return null
  // 自分で決める時間がなかった頃に保存された状態は、既定の時間で補う
  const common = { preset: o.preset, custom: isValidCustom(o.custom) ? o.custom : DEFAULT_CUSTOM }
  if (o.phase === 'idle') return { phase: 'idle', ...common }
  if (!isNum(o.startedAt) || !isNum(o.plannedMinutes) || o.plannedMinutes <= 0 || o.plannedMinutes > 180)
    return null
  const base = { ...common, startedAt: o.startedAt, plannedMinutes: o.plannedMinutes }
  if (o.phase === 'focus') return { phase: 'focus', ...base }
  if (o.phase === 'rest') return { phase: 'rest', ...base }
  if (o.phase === 'done' && isNum(o.endedAt)) {
    const done = { phase: 'done' as const, ...base, endedAt: o.endedAt }
    return isSavedLog(o.saved) ? { ...done, saved: o.saved } : done
  }
  return null
}

export function remainingMs(s: TimerState, now: number): number {
  switch (s.phase) {
    case 'idle':
      return durationsOf(s).focus * 60_000
    case 'done':
      return 0
    default:
      return Math.max(0, s.startedAt + s.plannedMinutes * 60_000 - now)
  }
}
